import { useEffect, useRef, useState } from "react";
import { Mic, Square } from "lucide-react";
import { toast } from "sonner";

const toWav = async (blob) => {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
  const rate = 16000;
  const off = new OfflineAudioContext(1, Math.ceil(buf.duration * rate), rate);
  const src = off.createBufferSource(); src.buffer = buf; src.connect(off.destination); src.start();
  const pcm = (await off.startRendering()).getChannelData(0);
  ctx.close();
  const view = new DataView(new ArrayBuffer(44 + pcm.length * 2));
  const str = (o, s) => [...s].forEach((c, i) => view.setUint8(o + i, c.charCodeAt(0)));
  str(0, "RIFF"); view.setUint32(4, 36 + pcm.length * 2, true); str(8, "WAVEfmt "); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true); str(36, "data"); view.setUint32(40, pcm.length * 2, true);
  pcm.forEach((v, i) => view.setInt16(44 + i * 2, Math.max(-1, Math.min(1, v)) * 0x7fff, true));
  return new File([view], `greeting-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.wav`, { type: "audio/wav" });
};

/* Record a greeting with the computer/phone mic and hand back a WAV file. */
export const MicRecorder = ({ onFile, disabled, testId }) => {
  const [rec, setRec] = useState(null);
  const [secs, setSecs] = useState(0);
  const chunks = useRef([]);
  useEffect(() => { if (!rec) return undefined; const i = setInterval(() => setSecs(s => s + 1), 1000); return () => clearInterval(i); }, [rec]);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream);
      chunks.current = [];
      r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      r.onstop = async () => { stream.getTracks().forEach(t => t.stop()); try { onFile(await toWav(new Blob(chunks.current, { type: r.mimeType }))); } catch { toast.error("Couldn't process the recording — try again"); } };
      r.start(); setSecs(0); setRec(r);
    } catch { toast.error("Allow microphone access to record a greeting"); }
  };
  const stop = () => { rec?.stop(); setRec(null); };
  return rec
    ? <button type="button" onClick={stop} className="text-sm font-semibold bg-red-600 hover:bg-red-500 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5" data-testid={`${testId}-rec-stop`}><Square className="w-3.5 h-3.5 fill-current" />Stop · {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}<span className="w-2 h-2 rounded-full bg-white animate-pulse" /></button>
    : <button type="button" onClick={start} disabled={disabled} className="text-sm font-semibold border border-slate-200 hover:bg-slate-50 px-3 py-1.5 rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid={`${testId}-rec-start`}><Mic className="w-4 h-4 text-red-600" />Record with my mic</button>;
};
